import { useApolloClient, useMutation } from '@apollo/client';
import type { UseEditProvider } from '@openx/components-provider/lib/utils/context/ProviderHookTypes.ts';
import { useSnackbar } from 'notistack';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import {
  EditProviderDocument,
  type EditProviderMutationVariables,
} from '../../../graphql/mutation/providers/EditProvider.generated.tsx';

export const useEditProvider: UseEditProvider = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const apolloClient = useApolloClient();

  const [editProviderMutation] = useMutation(EditProviderDocument, {
    refetchQueries: ['GetProvidersList'],
  });

  const editProvider = useCallback(
    (formData: EditProviderMutationVariables) => {
      editProviderMutation({
        onCompleted: () => {
          apolloClient.cache.evict({ fieldName: 'provider', id: 'ROOT_QUERY' });
          enqueueSnackbar(t('Provider edited successfully'), { variant: 'success' });
          navigate('/providers');
        },
        onError: ({ graphQLErrors }) => {
          const defaultErrorMessage = t('Failed to edit provider');
          const message = graphQLErrors[0]?.message;

          enqueueSnackbar(message ?? defaultErrorMessage, { variant: 'error' });
        },
        variables: formData,
      });
    },
    [apolloClient, editProviderMutation, enqueueSnackbar, navigate, t]
  );

  return { editProvider };
};
